// Formats the RUM web-vitals beacon query string. nginx logs the query string
// and an offline parser reads it, so every value is an integer or a short,
// low-cardinality token. No user data, no UA, no ids, no text content.

export interface BeaconEnv {
  ua: string;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  routePrefix: string;
}

export interface BeaconMetric {
  name: string;
  value: number;
  rating: string;
  attribution?: unknown;
}

const MAX_BEACON_BYTES = 400;
const MAX_LABEL = 32;
const MAX_TOKEN = 20;

// First path segments of the SPA routes (confs/routes.tsx). The SPA has a
// catch-all route, so any other segment (junk links, ids) maps to /other.
const KNOWN_ROUTE_PREFIXES = new Set([
  '/',
  '/event',
  '/market',
  '/user',
  '/profile',
  '/notification',
  '/rewards',
  '/leaderboard',
  '/how-it-works',
  '/pdf',
  '/preview',
]);

const routeBucket = (prefix: string): string => (KNOWN_ROUTE_PREFIXES.has(prefix) ? prefix : '/other');

// Case-sensitive app tokens of wallet in-app browsers.
const WALLET_UA =
  /TokenPocket|OKApp|OKX|BinanceWallet|BitKeep|Bitget|imToken|TrustWallet|Trust\/|MetaMaskMobile|SafePal|CoinbaseWallet|Bybit|Phantom|Rabby|\bGate/;

// 1 = named wallet in-app browser, 2 = generic iOS / Android WebView (a wallet
// without a UA marker, or another app), 0 = everything else.
export const webViewKind = (ua: string): 0 | 1 | 2 => {
  const s = ua || '';
  if (WALLET_UA.test(s)) return 1;
  if (/iPhone|iPad/.test(s) && !s.includes('Safari/')) return 2;
  if (s.includes('; wv)')) return 2;
  return 0;
};

// True when a dynamic-import error names the web-vitals chunk. main.tsx uses
// it to skip the chunk-reload handler: a missing RUM chunk must not reload the
// page. Safari's message has no URL, so there the reload guard still applies.
export const isWebVitalsChunkError = (e: unknown): boolean => {
  try {
    const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
    return /web-vitals/.test(msg);
  } catch {
    return false;
  }
};

// Reduces a target label or a web-vitals selector string to a safe token.
// Takes the last selector segment and keeps its tag part only. Strips hex
// (0x...) and every digit so dynamic ids cannot leak into the log. h1-h6 are
// the only labels that keep a digit.
export const sanitizeTargetLabel = (raw: unknown): string => {
  if (typeof raw !== 'string') return '-';
  const segments = raw.split(/[>\s]+/).filter(Boolean);
  const last = segments[segments.length - 1] ?? '';
  // An id-only segment carries no tag; never send the id.
  if (last.startsWith('#')) return '-';
  let s = last.split(/[.#[:]/)[0].toLowerCase();
  if (/^h[1-6]$/.test(s)) return s;
  s = s
    .replace(/0x[0-9a-f]*/g, '')
    .replace(/\d+/g, '')
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_LABEL)
    .replace(/-+$/, '');
  return s || '-';
};

type ElementLike = {
  nodeType?: number;
  tagName?: unknown;
  parentElement?: ElementLike | null;
  closest?: (sel: string) => { getAttribute?: (k: string) => string | null } | null;
};

// Passed to web-vitals as `generateTarget`. Uses the data-testid of the
// element or its closest ancestor, else the lowercased tag name. Never reads
// the id attribute or text content.
export const elementLabel = (node: unknown): string | undefined => {
  try {
    let el = node as ElementLike | null | undefined;
    if (el && el.nodeType !== 1) el = el.parentElement;
    if (!el || typeof el.tagName !== 'string') return undefined;
    const testId = typeof el.closest === 'function' ? el.closest('[data-testid]')?.getAttribute?.('data-testid') : null;
    return sanitizeTargetLabel(testId || el.tagName);
  } catch {
    return undefined;
  }
};

const token = (v: unknown): string | undefined =>
  typeof v === 'string' && v ? v.replace(/[^a-z-]/gi, '').slice(0, MAX_TOKEN) || undefined : undefined;

const int = (v: unknown): string | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? String(Math.round(v)) : undefined;

const attributionFields = (metric: BeaconMetric): [string, string | undefined][] => {
  const a = metric.attribution as Record<string, unknown> | null | undefined;
  if (!a || typeof a !== 'object') return [];
  switch (metric.name) {
    case 'INP':
      return [
        ['ld', token(a.loadState)],
        ['id', int(a.inputDelay)],
        ['pd', int(a.processingDuration)],
        ['pr', int(a.presentationDelay)],
        ['ev', token(a.interactionType)],
        ['tg', sanitizeTargetLabel(a.interactionTarget)],
      ];
    case 'LCP':
      return [
        ['ld', token(a.loadState)],
        ['tg', sanitizeTargetLabel(a.target)],
      ];
    case 'CLS':
      return [['tg', sanitizeTargetLabel(a.largestShiftTarget)]];
    default:
      return [];
  }
};

// URLSearchParams serializes to ASCII (percent-encoded), so the string length
// is the byte length. No TextEncoder: old WebViews do not have it.
const fits = (q: URLSearchParams) => `/wv?${q.toString()}`.length <= MAX_BEACON_BYTES;

export const buildWebVitalsBeacon = (metric: BeaconMetric, env: BeaconEnv): string => {
  const q = new URLSearchParams({
    m: String(metric.name).slice(0, MAX_TOKEN),
    // CLS is a unitless fraction; scale to keep the value an integer
    v: String(Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value)),
    r: String(metric.rating).slice(0, MAX_TOKEN),
    p: routeBucket(String(env.routePrefix)),
  });
  q.set('hc', int(env.hardwareConcurrency) ?? '-');
  q.set('dm', typeof env.deviceMemory === 'number' && Number.isFinite(env.deviceMemory) ? String(env.deviceMemory) : '-');
  q.set('wv', String(webViewKind(env.ua)));
  const base = q.toString();
  try {
    for (const [k, v] of attributionFields(metric)) if (v !== undefined) q.set(k, v);
    return fits(q) ? q.toString() : base;
  } catch {
    return base;
  }
};
